import type { EliteInsights } from '../../types/misc.types'

export type RebalanceInsightDisplay = {
  text: string
  tone: 'orange' | 'success'
}

function calendarDaysTillTimestamp(timestamp: number): number {
  const now = new Date()
  const target = new Date(timestamp)
  now.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
}

function remainingDaysText(epochMs: number): string {
  const days = calendarDaysTillTimestamp(epochMs)
  if (days <= 0) {
    return 'today'
  }
  if (days === 1) {
    return '1 day'
  }
  return `${days} days`
}

function formatFundCount(count: number, suffix: string): string {
  return `${count} ${count === 1 ? 'fund' : 'funds'} ${suffix}`
}

function isMultiplePlansRecent(eliteInsights: EliteInsights): boolean {
  if (eliteInsights.rebalancePlanCount == null || eliteInsights.rebalanceSnapshotDate == null) {
    return false
  }
  const daysUntilSnapshot = calendarDaysTillTimestamp(eliteInsights.rebalanceSnapshotDate)
  return daysUntilSnapshot > -30 && daysUntilSnapshot <= 0
}

export function buildRebalanceInsightDisplay(
  eliteInsights: EliteInsights | null | undefined,
  fundsToRebalanceFallback = 0
): RebalanceInsightDisplay | null {
  if (!eliteInsights?.userEligibleForRebalance) {
    return null
  }

  const isRecentPlan = isMultiplePlansRecent(eliteInsights)
  const fundsToRebalance = eliteInsights.numberOfFundsToRebalance ?? fundsToRebalanceFallback ?? 0
  const fundsExcluded = eliteInsights.numberOfFundsExcludedFromRebalance ?? 0
  const hasRebalanceFunds = fundsToRebalance > 0
  const hasExcludedFunds = fundsExcluded > 0

  let descriptionText: string | null = null
  let tone: RebalanceInsightDisplay['tone'] = 'orange'

  if (eliteInsights.rebalanceAvailable) {
    if (hasRebalanceFunds && hasExcludedFunds) {
      const rebalanceText = formatFundCount(fundsToRebalance, 'to rebalance')
      const excludedText = `${fundsExcluded} more ${fundsExcluded === 1 ? 'fund' : 'funds'} under review`
      descriptionText = isRecentPlan
        ? `New Plan Available — ${rebalanceText} and ${excludedText}`
        : `${rebalanceText} and ${excludedText}`
    } else if (hasRebalanceFunds) {
      descriptionText = isRecentPlan
        ? `New Plan Available — ${formatFundCount(fundsToRebalance, 'to rebalance')}`
        : formatFundCount(fundsToRebalance, 'to rebalance')
    } else if (hasExcludedFunds) {
      descriptionText = formatFundCount(fundsExcluded, 'under review')
    } else if (eliteInsights.userElite) {
      descriptionText = 'No funds to rebalance'
      tone = 'success'
    }
  } else if (eliteInsights.userElite) {
    const dropTime = eliteInsights.timeToRebalancePlanDrop
    if (dropTime != null && Date.now() < dropTime) {
      descriptionText = `Next plan in ${remainingDaysText(dropTime)}`
    }
  }

  if (!descriptionText) {
    return null
  }

  return { text: descriptionText, tone }
}
